/**
 * Blueprint Projection Model
 * Forward-looking trajectory story for specific time intervals
 */
export type BlueprintInterval = '60-day' | '90-day' | 'long-term';

export interface Blueprint {
  id: string;
  timeInterval: BlueprintInterval;
  trajectoryNarrative: string; // Must use conditional language
  patternBasis: string[];
  generatedAt: Date | string;
  relatedIntentions: string[];
  conditionalLanguage: boolean; // Ensures trajectory language is used
}

export function createBlueprint(
  timeInterval: BlueprintInterval,
  trajectoryNarrative: string,
  patternBasis: string[],
  relatedIntentions: string[]
): Blueprint {
  return {
    id: crypto.randomUUID(),
    timeInterval,
    trajectoryNarrative,
    patternBasis,
    generatedAt: new Date().toISOString(),
    relatedIntentions,
    conditionalLanguage: true,
  };
}

/**
 * Validate that trajectory narrative uses conditional language
 */
export function validateTrajectoryLanguage(narrative: string): boolean {
  const conditionalPhrases = [
    'if you continue',
    'based on your pattern data',
    'likely to develop',
    'if you maintain',
    'according to your patterns',
  ];

  const predictionPhrases = [
    'will happen',
    'is guaranteed',
    'is certain',
    'will definitely',
    'is predetermined',
  ];

  const hasConditional = conditionalPhrases.some((phrase) =>
    narrative.toLowerCase().includes(phrase)
  );
  const hasPrediction = predictionPhrases.some((phrase) =>
    narrative.toLowerCase().includes(phrase)
  );

  return hasConditional && !hasPrediction;
}
